import type { Href } from "expo-router";
import { router } from "expo-router";
import { useEffect, useState } from "react";
import {
  Linking,
  Platform,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
  type ViewStyle,
} from "react-native";
import { COLORS, FONTS } from "../lib/brand";

import { track } from "../lib/analytics";
import { MarketingHead } from "../components/MarketingHead";
import { META_FAQ } from "../lib/marketingMeta";

const FOREST = COLORS.FOREST.hex;
const FOREST_DEEP = COLORS.FOREST_DEEP.hex;
const AMBER = COLORS.AMBER.hex;
const LINEN = COLORS.LINEN.hex;

const TEXT_DIM = "rgba(237,232,223,0.62)";
const TEXT_FAINT = "rgba(237,232,223,0.40)";
const BORDER_SOFT = "rgba(237,232,223,0.08)";
const PAGE_BG = COLORS.FOREST_DEEPER.hex;

const BOOKINGS_URL = "https://outlook.office.com/book/CoravuIntake@Coravu.com/";

function openBookings(source: string) {
  track("discovery_call_cta_clicked", { source });
  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.open(BOOKINGS_URL, "_blank", "noopener,noreferrer");
  } else {
    void Linking.openURL(BOOKINGS_URL);
  }
}

type FaqItem = { q: string; a: string };
type FaqCategory = { title: string; items: FaqItem[] };

const CATEGORIES: FaqCategory[] = [
  {
    title: "For Companies",
    items: [
      {
        q: "What's included in a Licensing fee?",
        a: "Platform access for one competition: setup tools, student registration, deliverable submission, judging workflow, leaderboard, recruiting pipeline, and prize disbursement infrastructure. Per-student + per-submission pricing depending on competition size.",
      },
      {
        q: "What does Recruitment Management add?",
        a: "Coravu's team handles all operational execution: venue coordination, student outreach, judge sourcing, deliverable management, and competition-day staffing. You focus on selecting candidates; we handle everything else. Custom-priced based on scope.",
      },
      {
        q: "How do prize pools work?",
        a: "You set a prize amount when creating a competition. Coravu collects the prize pool from you upfront, holds it in a segregated account, and disburses directly to winning students after judging closes. We handle 1099 reporting for any winner over $600.",
      },
      {
        q: "Can I run a competition without recruiting?",
        a: "Yes. Set up the competition without linking positions and no opt-in flow appears at registration. You keep the performance data and judging insights without committing to interview offers.",
      },
      {
        q: "Who scores submissions?",
        a: "You select judges (Coravu can source industry judges if you don't have your own panel). Scoring happens across four weighted categories you configure: Team Efficiency, Deliverable Quality, Innovation & Solution, and Presentation & Communication.",
      },
    ],
  },
  {
    title: "For Students",
    items: [
      {
        q: "Is participation free?",
        a: "Yes — students never pay to participate. Your university or your student organization covers the platform; you just compete.",
      },
      {
        q: "What happens if I win?",
        a: "Coravu sends you the prize via your preferred payment method (ACH, Venmo, Zelle). For prizes over $600, we collect tax info via QuickBooks and issue a 1099 in January.",
      },
      {
        q: "Can companies see my work?",
        a: "Companies running the competition see ranked candidate results after judging closes — your weighted score, per-category breakdowns, and submission. They don't see individual judges' notes or in-progress drafts.",
      },
      {
        q: "Will I get an interview?",
        a: "Companies extend interview offers through Coravu after judging closes. You'll see them in your Interview Offers dashboard with deadline to respond. Accepting an offer opens scheduling outside the platform.",
      },
    ],
  },
  {
    title: "For Universities",
    items: [
      {
        q: "What's the partnership model?",
        a: "Coravu partners with universities to make the platform free for student organizations and to give the campus a single dashboard for all student-facing competitions. Email tyler@coravu.com to discuss a partnership.",
      },
      {
        q: "Is student data shared with companies?",
        a: "Only after a student opts in to a specific position during registration AND after judging closes. Profile info is limited to what students choose to share. Companies never receive raw contact info without student consent.",
      },
    ],
  },
];

export default function FaqScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 900;
  const pad = wide ? 64 : 22;

  useEffect(() => {
    track("faq_page_viewed");
  }, []);

  return (
    <>
      <MarketingHead meta={META_FAQ} />
      <ScrollView
        style={{ flex: 1, backgroundColor: PAGE_BG }}
        contentContainerStyle={{ paddingBottom: 64 }}
      >
        <View
          style={{
            paddingHorizontal: pad,
            paddingTop: 32,
            alignSelf: "center",
            width: "100%",
            maxWidth: 1080,
          }}
        >
          <Pressable
            onPress={() => router.replace("/" as Href)}
            style={[
              { alignSelf: "flex-start", paddingVertical: 4, marginBottom: 12 },
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyMedium,
                fontSize: 11.5,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: TEXT_FAINT,
              }}
            >
              ← Coravu
            </Text>
          </Pressable>

          {/* HERO */}
          <View style={{ marginTop: wide ? 28 : 16 }}>
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 11,
                letterSpacing: 2.4,
                textTransform: "uppercase",
                color: AMBER,
              }}
            >
              FAQ
            </Text>
            <Text
              style={{
                marginTop: 12,
                fontFamily: FONTS.display,
                fontSize: wide ? 64 : 44,
                lineHeight: wide ? 68 : 48,
                color: LINEN,
                letterSpacing: -1,
              }}
            >
              Frequently asked{" "}
              <Text style={{ fontStyle: "italic", color: AMBER }}>questions</Text>.
            </Text>
            <Text
              style={{
                marginTop: 16,
                fontFamily: FONTS.bodyLight,
                fontSize: wide ? 16 : 14,
                lineHeight: wide ? 26 : 23,
                color: TEXT_DIM,
                maxWidth: 720,
              }}
            >
              Quick answers about how Coravu works, pricing, and recruiting outcomes.
            </Text>
          </View>

          {CATEGORIES.map((cat) => (
            <CategoryBlock key={cat.title} category={cat} />
          ))}

          {/* CLOSING CTA */}
          <View
            style={{
              marginTop: 56,
              paddingHorizontal: wide ? 32 : 22,
              paddingVertical: wide ? 36 : 28,
              borderRadius: 8,
              backgroundColor: "rgba(181,135,58,0.06)",
              borderWidth: 1,
              borderColor: "rgba(181,135,58,0.32)",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.display,
                fontSize: wide ? 30 : 24,
                lineHeight: wide ? 36 : 30,
                color: LINEN,
                textAlign: "center",
              }}
            >
              More questions?
            </Text>
            <Text
              style={{
                marginTop: 8,
                fontFamily: FONTS.body,
                fontSize: 14,
                lineHeight: 22,
                color: TEXT_DIM,
                textAlign: "center",
                maxWidth: 520,
              }}
            >
              Email{" "}
              <Text
                onPress={() => {
                  if (Platform.OS === "web" && typeof window !== "undefined") {
                    window.location.href = "mailto:tyler@coravu.com";
                  } else {
                    void Linking.openURL("mailto:tyler@coravu.com");
                  }
                }}
                style={{ color: AMBER, fontFamily: FONTS.bodyMedium }}
              >
                tyler@coravu.com
              </Text>{" "}
              or book a discovery call.
            </Text>
            <View style={{ marginTop: 18 }}>
              <PrimaryCta
                label="Book a discovery call →"
                onPress={() => openBookings("faq_footer")}
                large
              />
            </View>
          </View>
        </View>
      </ScrollView>
    </>
  );
}

function CategoryBlock({ category }: { category: FaqCategory }) {
  return (
    <View>
      <Text
        style={{
          marginTop: 56,
          fontFamily: FONTS.display,
          fontSize: 32,
          lineHeight: 38,
          color: LINEN,
          letterSpacing: -0.5,
        }}
      >
        {category.title}
      </Text>
      <View
        style={{
          marginTop: 18,
          borderRadius: 6,
          backgroundColor: FOREST_DEEP,
          borderWidth: 1,
          borderColor: BORDER_SOFT,
          maxWidth: 760,
          overflow: "hidden",
        }}
      >
        {category.items.map((item, i) => (
          <FaqRow
            key={item.q}
            item={item}
            isLast={i === category.items.length - 1}
          />
        ))}
      </View>
    </View>
  );
}

function FaqRow({ item, isLast }: { item: FaqItem; isLast: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <Pressable
      onPress={() => setOpen((v) => !v)}
      style={[
        {
          paddingHorizontal: 18,
          paddingVertical: 16,
          borderBottomWidth: isLast ? 0 : 1,
          borderBottomColor: BORDER_SOFT,
        },
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
        <Text
          style={{
            flex: 1,
            fontFamily: FONTS.bodyMedium,
            fontSize: 14,
            color: LINEN,
          }}
        >
          {item.q}
        </Text>
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 14,
            color: AMBER,
            width: 14,
            textAlign: "center",
          }}
        >
          {open ? "−" : "+"}
        </Text>
      </View>
      {open ? (
        <Text
          style={{
            marginTop: 10,
            fontFamily: FONTS.body,
            fontSize: 13,
            lineHeight: 21,
            color: TEXT_DIM,
          }}
        >
          {item.a}
        </Text>
      ) : null}
    </Pressable>
  );
}

function PrimaryCta({
  label,
  onPress,
  large,
}: {
  label: string;
  onPress: () => void;
  large?: boolean;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={({ hovered, pressed }) => [
        {
          alignSelf: "flex-start",
          paddingHorizontal: large ? 22 : 18,
          paddingVertical: large ? 13 : 11,
          borderRadius: 4,
          backgroundColor: AMBER,
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
        Platform.OS === "web" && hovered
          ? ({ backgroundColor: COLORS.AMBER_WARM.hex, transform: [{ translateY: -1 }] } as ViewStyle)
          : null,
        pressed ? ({ opacity: 0.92 } as ViewStyle) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: large ? 12 : 11.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: FOREST,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}
